import { CustomSelect } from '~/components/ui/custom-select'
import type { BoardCard, MonitoringIndicator } from '~/api/types'

/**
 * 카드 설정 폼 본문 — 타입별 입력(텍스트: 포맷·내용 / stat·timeseries·pie·table:
 * 지표·통계·집계 범위). 모달 셸은 부모가 `<Modal>`로 감싼다.
 */
export function CardConfigForm({
    card,
    indicators,
    onChange,
}: {
    card: BoardCard
    indicators: MonitoringIndicator[]
    onChange: (patch: Partial<BoardCard>) => void
}) {
    return (
        <div className='grid gap-2'>
            {card.type === 'text' ? (
                <>
                    <CustomSelect
                        ariaLabel='텍스트 포맷'
                        value={card.format ?? 'md'}
                        onValueChange={(v) =>
                            onChange({
                                format: v as BoardCard['format'],
                            })
                        }
                        containerClassName='w-full'
                        className='border-border h-7'
                        options={[
                            {
                                value: 'md',
                                label: '마크다운(md)',
                            },
                            {
                                value: 'html',
                                label: 'HTML',
                            },
                            {
                                value: 'txt',
                                label: '일반텍스트(txt)',
                            },
                            {
                                value: 'rst',
                                label: 'reStructuredText(rst)',
                            },
                        ]}
                    />
                    <textarea
                        value={card.text ?? ''}
                        onChange={(e) =>
                            onChange({
                                text: e.target.value,
                            })
                        }
                        placeholder='메모… (포맷에 따라 md/html/txt로 렌더)'
                        className='border-border bg-card text-foreground h-16 w-full resize-none rounded border px-1.5 py-1 text-xs'
                    />
                </>
            ) : (
                <>
                    <CustomSelect
                        ariaLabel='지표 선택'
                        value={card.fieldId ?? ''}
                        onValueChange={(v) => {
                            const ind = indicators.find((x) => x.fieldId === v)
                            onChange({
                                fieldId: v,
                                title: ind?.nameKo,
                            })
                        }}
                        placeholder='지표 선택…'
                        containerClassName='w-full'
                        className='border-border h-7'
                        searchable
                        searchPlaceholder='지표 검색…'
                        options={indicators.map((i) => ({
                            value: i.fieldId,
                            label: `${i.nameKo}${i.unit ? ` (${i.unit})` : ''}`,
                        }))}
                    />
                    {card.type === 'stat' ? (
                        <div className='flex gap-1'>
                            <CustomSelect
                                ariaLabel='통계 방식'
                                value={card.statMetric ?? 'avg'}
                                onValueChange={(v) =>
                                    onChange({
                                        statMetric:
                                            v as BoardCard['statMetric'],
                                    })
                                }
                                containerClassName='flex-1'
                                className='border-border h-7'
                                options={[
                                    {
                                        value: 'avg',
                                        label: '평균',
                                    },
                                    {
                                        value: 'median',
                                        label: '중앙값',
                                    },
                                    {
                                        value: 'last',
                                        label: '최근값',
                                    },
                                    {
                                        value: 'max',
                                        label: '최대값',
                                    },
                                    {
                                        value: 'min',
                                        label: '최소값',
                                    },
                                ]}
                            />
                            <CustomSelect
                                ariaLabel='집계 범위'
                                value={card.windowKind ?? 'all'}
                                onValueChange={(v) =>
                                    onChange({
                                        windowKind:
                                            v as BoardCard['windowKind'],
                                    })
                                }
                                containerClassName='flex-1'
                                className='border-border h-7'
                                options={[
                                    {
                                        value: 'all',
                                        label: '전체',
                                    },
                                    {
                                        value: 'count',
                                        label: '최근 N개',
                                    },
                                    {
                                        value: 'days',
                                        label: '최근 N일',
                                    },
                                ]}
                            />
                            {card.windowKind && card.windowKind !== 'all' ? (
                                <input
                                    type='number'
                                    min={1}
                                    value={card.windowN ?? 7}
                                    onChange={(e) =>
                                        onChange({
                                            windowN:
                                                Number(e.target.value) || 1,
                                        })
                                    }
                                    className='border-border bg-card text-foreground h-7 w-12 rounded border px-1 text-xs'
                                />
                            ) : null}
                        </div>
                    ) : null}
                </>
            )}
        </div>
    )
}
